/**
 * Require the presence of static props or functions in a class.
 * Works just like the 'implements' keyword, but for static props.
 *
 * @example
 * interface StaticProps {
 *   someStaticProp: number;
 *   someStaticFunc(num: number): string;
 * }
 *
 * // Ignore the ; it's just for fomratting in the comment
 * ;@staticImplements<StaticProps>()
 * // Error! someStaticFunc and someStaticProp are missing
 * class InvalidClass {
 *    someStaticProp: number = 5; // Notice we're missing the 'static' keyword
 * }
 *
 * // Adding the static props fixes the errors!
 * ;@staticImplements<StaticProps>()
 * class ValidClass {
 *   static someStaticProp: number = 5;
 *
 *   static someStaticFunc(num: number) {
 *     return number.toString();
 *   }
 * }
 */
export function staticImplements<StaticProps>() {
	return <U extends (new () => unknown) & StaticProps>(constructor: U) => constructor
}

/**
 * Evaluates the fields that aren't shared between the two types
 *
 * @example
 * interface Person {
 *   name: string;
 *   age: number;
 *   occupation: string;
 * }
 *
 * interface Animal {
 *   name: string;
 *   age: number;
 *   breed: string;
 * }
 *
 * type Result = UniqueFields<Person, Animal>; // 'occupation' | 'breed'
 */
export type UniqueFields<T, K> = Exclude<keyof T, keyof K> | Exclude<keyof K, keyof T>

/**
 * Merges a child type into the root type,
 * making all the fields of the child that aren't shared with the parent optional.
 *
 * @example
 * interface Person {
 *   name: string;
 *   age: number;
 *   occupation: string;
 * }
 *
 * interface Animal {
 *   name: string;
 *   age: number;
 *   breed: string;
 * }
 *
 * type Result = MergeTypesPartial<Person, Animal>; // { name: string; age: number; occupation: string; breed?: string }
 */
export type MergeTypesPartial<Root, Child> = Root & Partial<Pick<Child, Extract<UniqueFields<Root, Child>, keyof Child>>>

export type JustOneProp<T extends object> = {
	[K in keyof T]: { [P in K]: T[P] } & { [P in Exclude<keyof T, K>]?: never } extends infer O ? { [P in keyof O]: O[P] } : never
}[keyof T]

type Without<T, U> = { [P in Exclude<keyof T, keyof U>]?: never }

/**
 * Allow either T or U, but not both
 *
 * @example
 * interface A {
 *   name: string;
 *   age: number;
 * }
 *
 * interface B {
 *   red: number;
 *   green: number;
 *   blue: number;
 *   alpha: number;
 * }
 *
 * type Result = XOR<A, B>;
 * const example1: Result = {
 *   name: 'bla',
 *   age: 10,
 *   red: 20 // error!
 * }
 *
 * const example2: Result = {
 *   red: 20,
 *   blue: 30,
 *   green: 255,
 *   alpha: 0.9,
 *   name: 'bla', // error!
 * }
 *
 */
export type XOR<T, U> = (Without<T, U> & U) | (Without<U, T> & T)
